import React, { useEffect, useState } from 'react';
import { API, resolveImg } from '../lib/img';

const DEFAULTS = {
  hero_title: 'About the Book',
  hero_subtitle: 'A practical guide for leaders navigating organisational transformation in complex environments.',
  why_title: 'Why This Book Exists',
  why_text: '',
  key_concepts_title: 'Key Concepts',
  key_concepts: [],
  who_title: 'Who This Book Is For',
  who_audiences: []
};

const DEFAULT_COVER = 'https://customer-assets.emergentagent.com/job_balanced-change/artifacts/ljo556l7_4eea1690-43b4-468c-a2dd-3248c467d977.png';

const AboutBook = () => {
  const [c, setC] = useState(DEFAULTS);
  const [cover, setCover] = useState(DEFAULT_COVER);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/content/about_book`)
      .then((r) => r.json())
      .then((d) => { if (d && d.content) setC({ ...DEFAULTS, ...d.content }); })
      .catch(() => {});
    fetch(`${API}/api/settings/book-cover`)
      .then((r) => r.json())
      .then((d) => { if (d && d.book_cover_url) setCover(d.book_cover_url); })
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section
        className="spectrum-wash"
        style={{
          padding: '120px 40px 80px'
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '500',
              marginBottom: '24px',
              color: '#111111'
            }}
          >
            {c.hero_title}
          </h1>
          <p style={{ fontSize: '1.25rem', lineHeight: '1.8', color: '#333333' }}>
            {c.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Why This Book Exists - with Book Cover */}
      <section className="section-container" style={{ backgroundColor: '#FFFFFF' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ marginBottom: '60px' }}>{c.why_title}</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '60px',
              alignItems: 'center'
            }}
            className="book-intro-grid"
          >
            <style>{`
              @media (max-width: 768px) {
                .book-intro-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
            {/* Book Cover Image */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
              <img
                src={resolveImg(cover)}
                alt="The Goldilocks Zone Book Cover"
                style={{
                  width: '100%',
                  maxWidth: '420px',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
            {/* Text Content */}
            <div>
              {String(c.why_text || '').split(/\n+/).filter(p => p.trim()).map((para, i) => (
                <p key={i} style={{ fontSize: '1.15rem', lineHeight: '1.8', color: '#333333', marginBottom: '1.25rem' }}>
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Concepts */}
      <section style={{ backgroundColor: '#111111', padding: '100px 40px', color: '#F4F1E8' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '2.5rem',
              fontWeight: '500',
              textAlign: 'center',
              marginBottom: '60px',
              color: '#F4F1E8'
            }}
          >
            {c.key_concepts_title}
          </h2>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}
            className="key-concepts-grid"
          >
            <style>{`
              @media (max-width: 768px) {
                .key-concepts-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
            {(c.key_concepts || []).map((concept, index) => (
              <div
                key={index}
                style={{
                  padding: '32px 24px',
                  backgroundColor: '#1C1C1C',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(201, 162, 75, 0.15)';
                  e.currentTarget.style.borderColor = '#C9A24B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1C1C1C';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                    fontWeight: '500',
                    color: '#F4F1E8',
                    margin: 0,
                    lineHeight: '1.6'
                  }}
                >
                  {concept}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Book Is For */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '60px' }}>
            {c.who_title}
          </h2>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px' }}
          >
            {(c.who_audiences || []).map((audience, index) => (
              <div
                key={index}
                style={{
                  padding: '32px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E7E0D4',
                  borderLeft: '4px solid #C9A24B',
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(8px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <p style={{ fontSize: '1.05rem', fontWeight: '500', color: '#111111', margin: 0 }}>
                  {audience}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutBook;
